import { isEmpty } from 'lodash-es';
import React, { useState } from 'react';
import useCollapse from 'react-collapsed';

import Minus from 'components/Icons/Minus';
import Plus from 'components/Icons/Plus';

import { MenuItem } from '../interfaces';
import {
  ListWrapper,
  ItemWrapper,
  LabelWrapper,
  StyledText,
  StyledIconCollapsed,
  StyledIconExpanded,
  SubItemsWrapper
} from './styles';

export type ComponentProps = {
  /**
   * Collection of all menu items to show in the menu
   */
  items?: MenuItem[];
  /**
   * Optional key to supply for the rendered component
   */
  id?: string;
};

// NOTE: The <Item /> component was placed inside the list component file combined to avoid circular dependency warning
type ItemProps = {
  /**
   * item to render on screen
   */
  item: MenuItem;
  /**
   * index in list where item is at, starting at index "0"
   */
  index: number;
};

const Item: React.FC<ItemProps> = ({ item, index }) => {
  const { id, href, text, state, subItems, itemLevel } = item;
  const [isExpanded, setIsExpanded] = useState(() => state === 'Expanded');
  // Controlled state
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded });

  return (
    <ItemWrapper>
      <LabelWrapper id={id} itemLevel={itemLevel} isExpanded={isExpanded} index={index}>
        <StyledText
          isExpanded={isExpanded}
          itemLevel={itemLevel}
          href={href ? href : null}
          target='_self'>
          {text}
        </StyledText>
        {isExpanded
          ? !isEmpty(subItems) && (
              <StyledIconExpanded
                itemLevel={itemLevel}
                isExpanded={isExpanded}
                onClick={() => {
                  setIsExpanded(!isExpanded);
                }}>
                <Minus {...getToggleProps()} />
              </StyledIconExpanded>
            )
          : !isEmpty(subItems) && (
              <StyledIconCollapsed
                itemLevel={item.itemLevel}
                isExpanded={isExpanded}
                onClick={() => setIsExpanded(!isExpanded)}>
                <Plus {...getToggleProps()} />
              </StyledIconCollapsed>
            )}
      </LabelWrapper>
      {!isEmpty(subItems) && isExpanded && (
        <SubItemsWrapper {...getCollapseProps()}>
          <List items={subItems} />
        </SubItemsWrapper>
      )}
    </ItemWrapper>
  );
};

const List: React.FC<ComponentProps> = ({ items = [], id }) => {
  return (
    <ListWrapper key={id}>
      {items.map((item, index) => {
        const { id } = item;
        // ref: https://reactjs.org/warnings/special-props.html
        return <Item key={id} index={index} item={item} />;
      })}
    </ListWrapper>
  );
};

export default List;
